/*
 * Industry content — `de` (German, translated from the source locale).
 *
 * This is the international variant of every industry (the old per-field `*Intl`
 * copy, resolved): generic time-registration framing, no belgium-specific pains/
 * features, and `compliance.slug` / `relatedArticles` pointing only at articles
 * that exist under `/de/` (the 4 market-agnostic posts). `nl.ts` carries the
 * belgium variant. Resolved by `industriesFor("de")`. Pure data (NFR10).
 */
import type { Industry } from "./types";

export const INDUSTRIES_DE: Industry[] = [
  {
    slug: "construction",
    name: "Baugewerbe",
    headline: "Zeiterfassung für Bauteams — standortverifizierter Check-in, Projektstunden und Budgetkontrolle, in einer App",
    intro: "Teams über mehrere Baustellen verteilt, Stunden auf Papier gekritzelt, Subunternehmerstunden unmöglich abzugleichen. Done-it gibt jeder Baustelle einen wasserdichten digitalen Stundenzettel, der mit einem Klick in Ihre Lohnabrechnung fließt.",
    pains: [
      {
        title: "Keine zuverlässige Aufzeichnung, wer wo gearbeitet hat",
        body: "Mit mehreren aktiven Baustellen und wechselnden Teams sind manuelle Stundenzettel von Natur aus fehlerhaft. Mitarbeitende runden auf, vergessen Einträge oder tragen sie Tage später ein — und Sie bleiben mit Lohnzahlen zurück, die Sie nicht verteidigen können.",
      },
      {
        title: "Budgetüberschreitungen tauchen zu spät auf",
        body: "Wenn Sie bemerken, dass eine Baustelle ihre geplanten Stunden aufgebraucht hat, ist die Marge bereits weg. Es gibt keinen Live-Countdown — nur eine böse Überraschung bei der Rechnungsstellung.",
      },
      {
        title: "Die Lohnvorbereitung frisst jeden Monat Stunden",
        body: "Stundenzettel einsammeln, mit den Projekten abgleichen und den Lohnexport vorbereiten dauert Tage. Jeder Eintrag ist ein potenzieller Fehler.",
      },
    ],
    features: [
      {
        title: "Standortverifizierter Check-in pro Baustelle",
        body: "Ein Tippen checkt den Mitarbeitenden ein, standortverifiziert, und verknüpft die Stunden mit dem richtigen Projekt. Keine Kartenleser, keine Terminals — nur das Smartphone, das Ihr Team ohnehin schon dabei hat.",
      },
      {
        title: "Live-Restsstunden pro Projekt",
        body: "Legen Sie beim Eröffnen des Projekts die geplanten Stunden als Budget fest. Mitarbeitende sehen einen Countdown des Verbleibenden; Sie sehen es live in Ihrem Dashboard. Budgetüberschreitungen sind sichtbar, bevor sie eintreten.",
      },
      {
        title: "Subunternehmerverwaltung und Anwesenheit",
        body: "Fügen Sie Subunternehmer zu einem Projekt hinzu, weisen Sie sie dem Check-in zu und erfassen Sie ihre Stunden neben Ihrem eigenen Team — in derselben Übersicht.",
      },
      {
        title: "Lohnexport mit einem Klick",
        body: "Alle erfassten Stunden — pro Mitarbeitendem, pro Projekt — werden in eine saubere Tabelle für Ihren Lohnabrechnungsanbieter exportiert. Keine manuelle Vorbereitung, keine Fehler.",
      },
    ],
    compliance: {
      label: "Digitale Zeiterfassung",
      title: "Standortverifizierter Check-in für Baustellen entwickelt",
      body: "Done-it bietet standortverifizierten Check-in, projektbezogene Zeiterfassung und einen sauberen Lohnexport für Bauteams. Für Teams, die auf belgischen Baustellen tätig sind, speist es zudem automatisch Check-in am Arbeitsplatz.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Team kommt auf der Baustelle an", body: "Jeder Mitarbeitende tippt einmal zum Einchecken. GPS bestätigt den Standort, versieht den Eintrag mit einem Zeitstempel und verknüpft die Stunden mit dem Projekt. Kein Papier, kein Terminal." },
      { step: "Sie sehen alles live", body: "Stunden, verbleibendes Budget, Subunternehmer-Anwesenheit und alle von der Baustelle gemeldeten zusätzlichen Arbeiten — alles in Ihrem Dashboard, in Echtzeit." },
      { step: "Monatsende in Minuten", body: "Exportieren Sie alle Stunden pro Mitarbeitendem und pro Projekt. Senden Sie sie an Ihren Lohnabrechnungsanbieter. Done-it erstellt automatisch saubere, prüfungsbereite Berichte." },
    ],
    stats: [
      { value: "1 Tippen", label: "zum Einchecken auf jeder Baustelle — kein Terminal, kein Papier" },
      { value: "1 Klick", label: "Lohnexport, bereit für Ihren Anbieter" },
      { value: "Live", label: "Ansicht der Reststunden pro Projekt und Baustelle" },
    ],
    quote: {
      text: "Unser Team checkt sich vor Ort selbst ein. Zum Monatsende geht die Lohnabrechnung einfach auf — wir jagen Stundenzetteln überhaupt nicht mehr hinterher.",
      who: "Inhaber, Bauunternehmen · 14 Mitarbeitende",
      result: "5 Stunden pro Woche bei der Verwaltung gespart",
    },
    relatedSlugs: ["installation", "maintenance", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "cleaning",
    name: "Reinigung",
    headline: "Zeiterfassung für Reinigungsunternehmen — standortverifizierter Check-in, Anwesenheitsnachweis und Lohnabrechnung in einer App",
    intro: "Reinigungskräfte sind den ganzen Tag zwischen Standorten unterwegs. Done-it erfasst in Echtzeit, wer wo ist — standortverifizierter Check-in an jedem Standort, automatischer Pausenabzug und ein sauberer Lohnexport zum Monatsende.",
    pains: [
      {
        title: "Anwesenheit an jedem Standort nachweisen",
        body: "Kunden und Kontrolleure erwarten wasserdichte Anwesenheitsaufzeichnungen. Manuelle Zettel können angefochten werden; standortverifizierte digitale Check-ins nicht.",
      },
      {
        title: "Teams über viele Standorte verteilt",
        body: "Da sich die Reinigungskräfte den ganzen Tag zwischen Standorten bewegen, haben Sie keine Live-Ansicht darüber, wer wo arbeitet — bis jemand anruft.",
      },
      {
        title: "Lohnfehler durch gerundete Stunden",
        body: "Ohne präzise Ein-/Auscheckzeiten wird die Lohnabrechnung aus dem Gedächtnis rekonstruiert. Stunden werden aufgerundet, Kosten steigen und Streitigkeiten folgen.",
      },
    ],
    features: [
      {
        title: "Echtzeit-Check-in und Check-out",
        body: "Mitarbeitende tippen sich an jedem Standort auf ihrem Telefon ein und aus, standortverifiziert; die Zeit ist exakt, mit Pausenerfassung.",
        plan: "Time Basic",
      },
      {
        title: "Automatischer Pausenabzug",
        body: "Vorgeschriebene Pausen werden automatisch vom Arbeitstag abgezogen — keine manuelle Rechnerei, keine Streitigkeiten.",
        plan: "Time Basic",
      },
      {
        title: "Live-Übersicht über mehrere Standorte",
        body: "Sehen Sie, welche Reinigungskräfte gerade an welchen Standorten sind. Erkennen Sie Lücken, bevor Kunden es tun.",
      },
      {
        title: "Abwesenheitserfassung mit digitalen Krankmeldungen",
        body: "Mitarbeitende laden Krankmeldungen und Abwesenheitsdokumente direkt aus der App hoch. Erstellen Sie die Abwesenheitsarten, die Ihr Team tatsächlich nutzt.",
      },
      {
        title: "Lohnexport mit einem Klick",
        body: "Exakte Zeitstempel fließen direkt in einen lohnfertigen Export für Ihren Anbieter. Keine Übertragung, keine Fehler.",
      },
    ],
    compliance: {
      label: "Standortverifizierte Anwesenheit",
      title: "Echtzeit-Anwesenheit an jedem Reinigungsstandort",
      body: "Done-it gibt Reinigungsteams standortverifizierten Check-in an jedem Standort, automatischen Pausenabzug und eine Live-Ansicht darüber, wer gerade an welchem Standort ist.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Reinigungskraft kommt am Standort an", body: "Ein Tippen checkt sie ein. GPS protokolliert den Standort und versieht den Eintrag mit einem Zeitstempel. Die Pausenerfassung ist in die App integriert." },
      { step: "Sie sehen jeden Standort live", body: "Wer gerade an welchem Standort ist — keine Anrufe, kein Rätselraten. Erkennen Sie Lücken in der Abdeckung, bevor Kunden es tun." },
      { step: "Lohnabrechnung mit einem Klick", body: "Alle Anwesenheitsdaten — exakte Zeiten, Standorte und Pausen — werden sauber für Ihren Lohnabrechnungsanbieter exportiert." },
    ],
    stats: [
      { value: "1 Tippen", label: "zum Einchecken an jedem Standort — kein Terminal nötig" },
      { value: "Live", label: "Ansicht darüber, wer gerade an welchem Standort ist" },
      { value: "0", label: "Doppeleinträge — Stunden fließen direkt in die Lohnabrechnung" },
    ],
    quote: {
      text: "Wir können endlich nachweisen, wer wo und wann gereinigt hat. Keine Anwesenheitszettel mehr, keine Kundenstreitigkeiten mehr — GPS erledigt das.",
      who: "Manager, Reinigungsunternehmen · 9 Mitarbeitende",
      result: "Null Aufrunden von Stunden",
    },
    relatedSlugs: ["maintenance", "field-service", "construction"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "installation",
    name: "Installation",
    headline: "Zeiterfassung für Installationsteams — Projektstunden, Navigation und Budgetkontrolle in einer App",
    intro: "Schicken Sie Ihre Monteure mit allem, was sie brauchen, zur richtigen Adresse, verknüpfen Sie jede geleistete Stunde automatisch mit dem richtigen Auftrag und sehen Sie das verbleibende Budget, bevor die Baustelle schließt. Done-it ist der einfachste Weg, Installationsprojekte termin- und kostengerecht zu halten.",
    pains: [
      {
        title: "Auftragsinformationen erreichen das Feld nicht rechtzeitig",
        body: "Teams kommen ohne Adresse, Ansprechpartner oder Details zum Auftragsumfang an einer Baustelle an. Dieser erste Anruf sind zehn Minuten abrechenbarer Zeit, die verloren gehen, bevor auch nur eine Stunde gearbeitet wurde.",
      },
      {
        title: "Stunden sind nicht mit dem richtigen Projekt verknüpft",
        body: "Wenn Mitarbeitende ihre Stunden am Ende des Tages manuell eintragen — sofern sie sie überhaupt eintragen — landen die Stunden auf dem falschen Auftrag oder verschwinden ganz. Die Rechnungen stimmen nicht mit der Realität überein.",
      },
      {
        title: "Budgetüberschreitungen zu spät erkannt",
        body: "Ohne einen Live-Countdown der verbleibenden Stunden pro Projekt erfahren Sie erst beim Abschluss der Rechnung, dass ein Auftrag überzogen wurde — nicht, solange Sie noch reagieren können.",
      },
      {
        title: "Subunternehmerstunden schwer zu erfassen",
        body: "Subunternehmer, die auf derselben Baustelle arbeiten, nutzen unterschiedliche Systeme, tragen Stunden separat oder gar nicht ein. Der Abgleich zum Monatsende ist ein langsamer, fehleranfälliger Prozess.",
      },
    ],
    features: [
      {
        title: "Projektinformationen direkt in die App geteilt",
        body: "Mitarbeitende öffnen das Projekt auf ihrem Telefon: Adresse, geplante Stunden, Ansprechpartner und hochgeladene Dateien (Pläne, Anleitungen, PDFs). Alles, was sie brauchen, ist da, bevor sie ankommen.",
        plan: "Project Basic",
      },
      {
        title: "Navigation zum Standort per Knopfdruck",
        body: "Tippen Sie einmal und das Telefon navigiert zur Projektadresse — in die App integriert, keine separate Karten-App nötig.",
      },
      {
        title: "Countdown der verbleibenden Stunden pro Projekt",
        body: "Legen Sie beim Eröffnen des Projekts die geplanten Stunden fest. Mitarbeitende sehen, wie viele Stunden übrig sind; Sie sehen den Live-Status in Ihrem Dashboard. Überschreitungen sind sichtbar, bevor sie eintreten.",
        plan: "Project Basic",
      },
      {
        title: "Mitarbeiter-Check-in pro Projekt",
        body: "Jeder Mitarbeitende checkt am richtigen Projekt ein. Sie wissen immer, wer gerade an welchem Standort ist.",
        plan: "Project Basic",
      },
      {
        title: "Erfassung zusätzlicher Arbeiten & Materialien",
        body: "Wenn der Umfang vor Ort wächst, erfassen Mitarbeitende zusätzliche Arbeiten und Materialien in Echtzeit — mit Text und Fotos — sodass nichts aus dem Gedächtnis abgerechnet wird.",
        plan: "Project Basic",
      },
    ],
    compliance: {
      label: "Digitale Zeiterfassung",
      title: "Projektbezogener standortverifizierter Check-in für Installationsteams",
      body: "Done-it gibt Installationsteams standortverifizierten Check-in pro Projekt, einen Live-Countdown der verbleibenden Stunden und automatischen Lohnexport — und deckt damit jede lokale Anforderung an die Zeiterfassung ab. Für Teams, die auf belgischen Baustellen arbeiten, lässt es sich zudem mit CheckIn@Work integrieren.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Mitarbeitender öffnet das Projekt auf seinem Telefon", body: "Adresse, Kontakt, Umfang und Datei-Uploads sind direkt vorhanden. Sie navigieren mit einem Tippen — keine Anrufe ins Büro nach dem Weg." },
      { step: "Stunden werden automatisch dem richtigen Auftrag zugeordnet", body: "Der Check-in verknüpft jede geleistete Minute mit dem korrekten Projekt. Das verbleibende Budget aktualisiert sich in Echtzeit." },
      { step: "Zusätzliche Arbeiten werden vor Ort erfasst", body: "Umfangsänderungen, zusätzliche Materialien und Verzögerungen werden in dem Moment, in dem sie anfallen, mit Text und Fotos erfasst — bereit für die Rechnung." },
    ],
    stats: [
      { value: "Echtzeit", label: "Ansicht der verbleibenden Stunden pro Projekt" },
      { value: "0", label: "Anrufe ins Büro nach Baustellenadressen" },
      { value: "1 Klick", label: "Navigation zu jeder Projektadresse aus der App" },
    ],
    quote: {
      text: "Stunden landen automatisch auf dem richtigen Projekt. Die Projektkalkulation ist plötzlich einfach — wir sehen die echte Marge, bevor wir einen Auftrag abschließen.",
      who: "Inhaber, Installationsbetrieb · 6 Mitarbeitende",
      result: "Echte Kosten pro Auftrag, live",
    },
    relatedSlugs: ["construction", "electricians", "hvac"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "field-service",
    name: "Außendienst",
    headline: "Zeiterfassung für Außendienstteams — Kilometer, Spesen und Stunden von unterwegs, alles in einer App",
    intro: "Techniker, die mehrere Kunden am Tag besuchen, Belege in Handschuhfächern, Kilometer aus dem Gedächtnis erfasst. Done-it erfasst Stunden, Kilometer und Spesen im Feld — dem richtigen Auftrag zugeordnet in dem Moment, in dem sie anfallen.",
    pains: [
      {
        title: "Kilometer erst Tage später erfasst — wenn überhaupt",
        body: "Techniker zu bitten, ihre Routen am Ende der Woche zu rekonstruieren, bedeutet ungenaue Erstattungen, Streitigkeiten mit Kunden und einen wachsenden Stapel nicht abgerechneter Kilometer.",
      },
      {
        title: "Spesen auf Papierbelegen, die verschwinden",
        body: "Ein Beleg im Handschuhfach ist eine Kostenposition, die es vielleicht nie in eine Rechnung schafft. Die echten Kosten pro Auftrag sind immer zu niedrig angesetzt.",
      },
      {
        title: "Keine Echtzeit-Ansicht, wo das Team ist",
        body: "Ohne Live-Check-ins erfahren Sie erst, wo ein Techniker ist, wenn etwas schiefgeht und jemand anruft.",
      },
      {
        title: "Schwer, die tatsächliche Marge pro Auftrag zu sehen",
        body: "Wenn Arbeit, Anfahrt und Spesen in separaten Systemen erfasst werden — oder gar nicht — gibt es nie eine einzige Kostenzahl pro Auftrag.",
      },
    ],
    features: [
      {
        title: "Automatische Kilometererfassung (Trax-it)",
        body: "GPS protokolliert Abfahrt, Ankunft und exakte Distanz pro Fahrt. Konfigurieren Sie Transportarten (eigenes Auto, Firmenfahrzeug, Fahrgemeinschaft) mit jeweils eigenen Erstattungssätzen. Kein manuelles Fahrtenbuch.",
        plan: "Add-on",
      },
      {
        title: "Spesenverwaltung vor Ort",
        body: "Mitarbeitende fotografieren einen Beleg im Feld; er wird dem Auftrag zugeordnet und ist erstattungsbereit — und wird nicht zum Monatsende hinterhergejagt.",
        plan: "Time Basic",
      },
      {
        title: "Projektgebundene Kostenkontrolle",
        body: "Stunden, Kilometer und Spesen werden in Echtzeit gegen den Auftrag aufsummiert. Die Kosten pro Auftrag sind sichtbar, solange der Auftrag noch offen ist.",
        plan: "Project Basic",
      },
      {
        title: "Standortverifizierter Check-in und Live-Teamübersicht",
        body: "Jeder Techniker checkt am Kundenstandort ein. Sie sehen, wer gerade wo ist — keine Anrufe, kein Rätselraten.",
      },
      {
        title: "Lohnexport mit einem Klick",
        body: "Stunden und Kilometererstattungen werden sauber für Ihren Lohnabrechnungsanbieter exportiert — mit Ihren konfigurierten Kilometersätzen, die automatisch angewendet werden.",
      },
    ],
    compliance: {
      label: "Digitale Zeit- & Kilometererfassung",
      title: "GPS-Stunden und automatische Kilometer für Außendienstteams",
      body: "Done-it erfasst Kilometer automatisch pro Fahrt, wendet Ihre konfigurierten Erstattungssätze an und protokolliert jeden Kilometer beim richtigen Kunden oder Projekt. Das EU-Urteil Tyco bedeutet, dass die Fahrt von zu Hause zum ersten Kunden als Arbeitszeit zählt — Done-it erfasst auch das.",
      slug: "how-to-track-mileage-for-field-teams",
    },
    workflow: [
      { step: "Techniker fährt zum ersten Einsatz los", body: "Der Check-in startet das Fahrtenprotokoll. Kilometer werden automatisch von der Abfahrt bis zur Ankunft erfasst — keine manuelle Eingabe." },
      { step: "Am Einsatzort", body: "Die Stunden werden mit dem richtigen Projekt verknüpft. Zusätzliche Arbeiten, Materialien und Belege werden in dem Moment in der App erfasst, in dem sie anfallen." },
      { step: "Monatsende ohne Chaos", body: "Jede Stunde, jeder Kilometer und jede Spesenposition ist bereits dem korrekten Auftrag zugeordnet. Exportieren Sie in die Lohnabrechnung und Rechnung mit echten Kostenzahlen." },
    ],
    stats: [
      { value: "Automatisch", label: "Kilometererfassung — kein manuelles Fahrtenbuch" },
      { value: "Live", label: "Kosten pro Auftrag: Stunden + Anfahrt + Spesen" },
      { value: "0", label: "verlorene Kilometer — jede Fahrt automatisch erfasst" },
    ],
    quote: {
      text: "Die Kilometer waren früher ein monatliches Ärgernis. Jetzt protokolliert es sich von selbst — jeder Kilometer ist dem richtigen Kunden zugeordnet, bevor wir überhaupt wieder im Büro sind.",
      who: "Betriebsleiter, Außendienstunternehmen · 11 Mitarbeitende",
      result: "Präzise Kosten pro Auftrag, jedes Mal",
    },
    relatedSlugs: ["hvac", "maintenance", "plumbing"],
    relatedArticles: [
      "how-to-track-mileage-for-field-teams",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },

  {
    slug: "hvac",
    name: "Heizung-Lüftung-Klima",
    headline: "Zeiterfassung für Heizungs-, Lüftungs- und Klimatechniker — Projektkalkulation, Teile und Anfahrt in einer App",
    intro: "Jeder Serviceeinsatz hat drei Kostenarten: Arbeit, Teile und Anfahrt. Done-it erfasst alle drei im Feld und summiert sie gegen den richtigen Auftrag — sodass Sie Ihre Marge kennen, bevor Sie die Rechnung abschließen.",
    pains: [
      {
        title: "Serviceeinsätze sind schwer präzise zu kalkulieren",
        body: "Arbeit, Teile und Anfahrt liegen in verschiedenen Systemen — oder in verschiedenen Taschen. Die echten Kosten eines Einsatzes werden erst lange klar, nachdem der Techniker den Standort verlassen hat.",
      },
      {
        title: "Kein Nachweis der Servicezeit",
        body: "Wenn ein Kunde anficht, wie lange eine Reparatur gedauert hat, ist ein Papier-Stundenzettel leicht anzufechten. GPS-bestätigte Check-in- und Check-out-Zeitstempel sind es nicht.",
      },
      {
        title: "Wartungsverträge zehren still an der Marge",
        body: "Bei wiederkehrenden Wartungsverträgen summieren sich kleine Überschreitungen über viele Einsätze. Ohne einen Live-Stundenzähler erfahren Sie erst bei der Verlängerung, dass der Vertrag unter Wasser steht.",
      },
      {
        title: "Techniker vergessen, Pausen zu erfassen",
        body: "Manuelle Zeiteinträge übersehen Pausen, was zu Überbezahlung und einer Lohnabrechnung führt, die nicht mit der Realität übereinstimmt.",
      },
    ],
    features: [
      {
        title: "Projektbasierte Zeiterfassung",
        body: "Jeder Serviceeinsatz oder Wartungsvertrag ist ein Projekt. Stunden, Anfahrt und Materialien werden in Echtzeit aufsummiert — und geben Ihnen Live-Einblick in die Marge.",
        plan: "Project Basic",
      },
      {
        title: "Zusätzliche Arbeiten und Materialien, vor Ort erfasst",
        body: "Wenn ein Auftrag wächst, erfassen Techniker zusätzliche Arbeiten und Teile mit Text und Fotos, bevor sie gehen. Nichts wird aus dem Gedächtnis abgerechnet.",
        plan: "Project Basic",
      },
      {
        title: "Kilometer und Transportkosten",
        body: "Kilometer pro Einsatz werden automatisch protokolliert und dem Auftrag zugerechnet. Konfigurieren Sie Erstattungssätze pro Fahrzeugtyp.",
        plan: "Add-on",
      },
      {
        title: "Automatischer Pausenabzug",
        body: "Vorgeschriebene Pausen werden automatisch von der Gesamtzeit abgezogen — keine Streitigkeiten, keine Lohnfehler.",
      },
      {
        title: "Standortverifizierter Check-in und Anwesenheitsnachweis",
        body: "Mit Zeitstempel versehener, GPS-bestätigter Check-in an jedem Standort. Unanfechtbarer Nachweis, wann der Techniker ankam und ging.",
      },
    ],
    compliance: {
      label: "Digitale Zeit- & Kostenerfassung",
      title: "Vollständige Auftragskalkulation für Heizung-Lüftung-Klima-Serviceeinsätze",
      body: "Done-it gibt Heizung-Lüftung-Klima-Teams standortverifizierten Check-in pro Serviceeinsatz, Live-Kostentransparenz pro Auftrag (Arbeit + Anfahrt + Teile) und einen sauberen Lohnexport — und erfüllt jede lokale Anforderung an die Zeiterfassung ohne zusätzliche Hardware.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Techniker checkt am Standort ein", body: "GPS versieht die Ankunft mit einem Zeitstempel und verknüpft die Stunden mit dem Serviceeinsatz oder Wartungsvertrag. Kein Papier, kein Terminal." },
      { step: "Zusätzliche Arbeiten werden direkt vor Ort erfasst", body: "Verwendete Teile, zusätzliche Stunden und Umfangsänderungen werden in der App mit Fotos erfasst — bevor der Standort verlassen wird." },
      { step: "Auftragskosten sofort sichtbar", body: "Arbeit, Anfahrt und Materialien summieren sich in Echtzeit gegen das Projekt. Sie sehen die Marge, bevor Sie die Rechnung ausstellen." },
    ],
    stats: [
      { value: "3", label: "erfasste Kostenarten pro Einsatz: Arbeit, Anfahrt, Teile" },
      { value: "Live", label: "Margentransparenz pro Servicevertrag" },
      { value: "0", label: "Überraschungen bei der Rechnungsstellung — Kosten in Echtzeit sichtbar" },
    ],
    quote: {
      text: "Wir wissen endlich, was uns jeder Servicevertrag tatsächlich kostet. Die Margenüberraschungen hörten an dem Tag auf, an dem wir Done-it einsetzten.",
      who: "Inhaber, Heizung-Lüftung-Klima-Unternehmen · 7 Techniker",
      result: "Echte Kosten pro Serviceeinsatz, live",
    },
    relatedSlugs: ["installation", "electricians", "plumbing"],
    relatedArticles: [
      "how-to-track-mileage-for-field-teams",
      "time-tracking-for-field-teams-complete-guide",
    ],
  },

  {
    slug: "electricians",
    name: "Elektriker",
    headline: "Zeiterfassung für Elektriker — Stunden pro Projekt, Budgetkontrolle und Navigation in einer App",
    intro: "Stunden pro Projekt, ein Live-Countdown des verbleibenden Budgets und Navigation zum Standort per Knopfdruck — entwickelt für kleine Elektroteams, die Projektkontrolle ohne Verwaltungsaufwand brauchen.",
    pains: [
      {
        title: "Stunden sind nicht mit dem richtigen Projekt verknüpft",
        body: "Wenn Elektriker in einer Woche über mehrere Aufträge hinweg arbeiten, führt die manuelle Zuordnung der Stunden am Ende des Tages zu falsch zugeordneten Kosten, Abrechnungsstreitigkeiten und einer Lohnabrechnung, die Sie nicht erklären können.",
      },
      {
        title: "Budgetüberschreitungen zu spät erkannt",
        body: "Ohne eine Live-Ansicht der verbrauchten vs. budgetierten Stunden erfahren Sie erst bei der Rechnungsstellung, dass ein Projekt überzogen wurde — nicht, als Sie noch hätten reagieren können.",
      },
      {
        title: "Zeit verschwendet bei der Standortsuche",
        body: "Adressen in eine separate App getippt, Anrufe ins Büro nach dem Weg, verspätete Ankünfte an Kundenstandorten. Jede verschwendete Minute ist eine verlorene abrechenbare Minute.",
      },
      {
        title: "Subunternehmerstunden schwer abzugleichen",
        body: "Bei größeren Installationen erfassen Subunternehmer-Elektriker ihre eigenen Stunden separat. Eine kombinierte Ansicht der Arbeitskosten am Ende eines Projekts zu erhalten, ist eine manuelle, fehleranfällige Aufgabe.",
      },
    ],
    features: [
      {
        title: "Projektstunden mit Live-Restbudget",
        body: "Jede geleistete Minute wird automatisch mit dem richtigen Projekt verknüpft. Ein Live-Countdown zeigt, wie viele Stunden gegen das Budget übrig sind — für Sie und für den Mitarbeitenden vor Ort.",
        plan: "Project Basic",
      },
      {
        title: "Navigation zum Standort per Knopfdruck",
        body: "Die Projektadresse ist in der App. Ein Tippen öffnet die Navigation — kein Suchen, keine separate Karten-App, keine Anrufe ins Büro.",
      },
      {
        title: "Mitarbeiter-Check-in pro Projekt",
        body: "Jeder Elektriker checkt am richtigen Auftrag ein. Sie wissen immer, wer gerade an welchem Standort ist.",
        plan: "Project Basic",
      },
      {
        title: "Zusätzliche Arbeiten vor Ort erfasst",
        body: "Wenn sich der Umfang ändert — ein unerwarteter Stromkreis, zusätzliche Materialien — wird es in der App mit Fotos erfasst, bevor das Team geht.",
        plan: "Project Basic",
      },
      {
        title: "Überstundenerfassung mit flexibler Vergütung",
        body: "Überstunden werden in dem Moment erfasst, in dem sie anfallen. Mitarbeitende wählen, ob sie ausgezahlt oder in Freizeit umgewandelt werden.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Projektbezogener standortverifizierter Check-in",
      title: "Live-Budgetkontrolle und standortverifizierter Check-in für Elektroteams",
      body: "Done-it gibt Elektrikern projektbezogenen standortverifizierten Check-in, ein Live-Stundenbudget und Lohnexport — und erfüllt jede lokale Anforderung an die Zeiterfassung. Für Teams auf belgischen Baustellen lässt es sich zudem mit CheckIn@Work integrieren.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Elektriker navigiert zum Auftrag", body: "Adresse und Umfang sind bereits in der App. Ein Tippen öffnet die Navigation — keine Anrufe, keine Verwirrung." },
      { step: "Stunden werden automatisch dem richtigen Projekt zugeordnet", body: "Der Check-in am Standort verknüpft jede geleistete Minute mit dem korrekten Projekt. Der Reststundenzähler aktualisiert sich live." },
      { step: "Umfangsänderungen bleiben in der Aufzeichnung", body: "Zusätzliche Arbeiten und Materialien werden vor Ort erfasst, bevor man geht. Die Rechnung spiegelt wider, was tatsächlich geschah." },
    ],
    stats: [
      { value: "Live", label: "Ansicht der verbleibenden Stunden pro Projekt" },
      { value: "0", label: "Anrufe ins Büro nach Baustellenadressen" },
      { value: "1 Tippen", label: "zum Einchecken an jedem Standort — kein Terminal nötig" },
    ],
    quote: {
      text: "Wir wissen in Echtzeit, ob ein Projekt das Budget überschreitet. Allein das hat uns dieses Jahr vor zwei bösen Überraschungen bewahrt.",
      who: "Inhaber, Elektrounternehmen · 5 Elektriker",
      result: "Budgetüberschreitungen erkannt, bevor sie eintreten",
    },
    relatedSlugs: ["installation", "hvac", "plumbing"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "how-to-track-mileage-for-field-teams",
    ],
  },

  {
    slug: "plumbing",
    name: "Sanitär",
    headline: "Zeiterfassung für Sanitärteams — schneller Check-in, präzise Lohnabrechnung und Anwesenheitsnachweis für jeden Einsatz",
    intro: "Notdiensteinsätze, mehrere Aufträge pro Tag, Stunden am Ende der Woche aus dem Gedächtnis eingetragen — oder gar nicht. Done-it macht daraus an jedem Standort eine Sache von einem Tippen und zum Monatsende eine Lohnabrechnung mit einem Klick.",
    pains: [
      {
        title: "Einsatzstunden aus dem Gedächtnis rekonstruiert",
        body: "Bei mehreren Aufträgen an einem Tag haben Installateure selten Zeit, präzise Stundenzettel auszufüllen. Stunden werden gerundet oder vergessen, und die Lohnabrechnung ist bestenfalls eine Schätzung.",
      },
      {
        title: "Kein Nachweis der Zeit vor Ort",
        body: "Wenn ein Kunde anficht, wie lange eine Reparatur gedauert hat, gibt es keine objektive Aufzeichnung. Ein GPS-bestätigter digitaler Check-in lässt sich leicht vorlegen — ein Papier-Stundenzettel nicht.",
      },
      {
        title: "Kilometer zwischen Aufträgen nicht erfasst",
        body: "Die Fahrten zwischen Einsätzen summieren sich über die Woche und den Monat, aber ohne automatische Erfassung werden sie selten korrekt abgerechnet oder erstattet.",
      },
      {
        title: "Die Lohnvorbereitung ist langsam",
        body: "Zum Monatsende kostet das Hinterherjagen von Stundenzetteln, das Korrigieren von Fehlern und das Erstellen des Lohnexports Zeit, die Ihr Verwaltungsteam nicht aufwenden sollte.",
      },
    ],
    features: [
      {
        title: "Standortverifizierter Check-in mit einem Tippen bei jedem Einsatz",
        body: "Ankunft und Abfahrt mit einem Tippen erfasst, mit Standortverifizierung des Standorts. Schnell genug für Notrufe; präzise genug für Streitigkeiten.",
        plan: "Time Basic",
      },
      {
        title: "Automatischer Pausenabzug",
        body: "Vorgeschriebene Pausen werden automatisch von der Gesamtzeit abgezogen — nichts auszufüllen, keine Lohnfehler.",
        plan: "Time Basic",
      },
      {
        title: "Kilometer pro Fahrt erfasst",
        body: "Konfigurieren Sie Transportarten und Erstattungssätze. Kilometer werden automatisch protokolliert und mit dem richtigen Kunden oder Projekt verknüpft.",
        plan: "Add-on",
      },
      {
        title: "Abwesenheits- und Krankmeldungsverwaltung",
        body: "Mitarbeitende reichen Krankmeldungen digital aus der App ein. Erstellen Sie die Abwesenheitsarten, die Ihr Team tatsächlich nutzt, einschließlich wetterbedingter Verzögerungen.",
      },
      {
        title: "Lohnexport mit einem Klick",
        body: "Alle Stunden- und Kilometerdaten werden sauber für Ihren Lohnabrechnungsanbieter exportiert — keine manuelle Vorbereitung, kein Excel.",
        plan: "Time Basic",
      },
    ],
    compliance: {
      label: "Digitale Zeiterfassung",
      title: "Schneller standortverifizierter Check-in für jeden Einsatz",
      body: "Done-it deckt jede lokale Anforderung an die Zeiterfassung für Sanitärteams ab — standortverifizierter Check-in mit einem Tippen pro Einsatz, automatischer Pausenabzug und ein sauberer Lohnexport. Keine Papier-Stundenzettel, keine manuelle Rekonstruktion.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Installateur kommt beim Einsatz an", body: "Ein Tippen checkt ein. GPS protokolliert Standort und Zeit. Schnell genug für einen Notfalleinsatz." },
      { step: "Abfahrt beim Gehen angetippt", body: "Der Check-out schließt den Zeiteintrag. Die Kilometer zum nächsten Auftrag beginnen automatisch zu protokollieren." },
      { step: "Monatsende mit einem Klick", body: "Alle Einsätze, Stunden und Kilometer werden sauber exportiert. Die Lohnvorbereitung geht von einem Tag auf Minuten zurück." },
    ],
    stats: [
      { value: "1 Tippen", label: "zum Einchecken — schnell genug für Notrufe" },
      { value: "1 Klick", label: "Lohnexport an Ihren Anbieter" },
      { value: "0", label: "aus dem Gedächtnis rekonstruierte Stunden zum Monatsende" },
    ],
    quote: {
      text: "Wir haben früher zum Monatsende einen ganzen Vormittag damit verbracht, Stunden zu rekonstruieren. Jetzt exportieren und senden wir einfach. Der Lohnabrechnungsanbieter bekommt es sauber.",
      who: "Inhaber, Sanitärunternehmen · 4 Installateure",
      result: "Lohnvorbereitung von Stunden auf Minuten reduziert",
    },
    relatedSlugs: ["hvac", "electricians", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "how-to-track-mileage-for-field-teams",
    ],
  },

  {
    slug: "landscaping",
    name: "Garten- und Landschaftsbau",
    headline: "Zeiterfassung für Garten- und Landschaftsbauunternehmen — Saisonteams, mehrere Standorte und Kilometer in einer Übersicht",
    intro: "Hochsaison bedeutet viele Standorte, wechselnde Teams und viele Kilometer. Done-it hält jede Stunde, jede Abwesenheit und jede Fahrt organisiert — sodass Sie sich auf die Arbeit konzentrieren können, nicht auf den Papierkram.",
    pains: [
      {
        title: "Saisonteams sind komplex zu verwalten",
        body: "Befristete Mitarbeitende, wechselnde Pläne und unterschiedliche Teamgrößen machen eine präzise Stundenerfassung mit Papier nahezu unmöglich. Wenn Sie zum Monatsende abgleichen, fehlt immer etwas.",
      },
      {
        title: "Stunden über viele Standorte verteilt",
        body: "Ein Team, das drei oder vier Standorte pro Tag besucht, erzeugt Stunden, die korrekt aufgeteilt werden müssen. Manuelle Eingabe führt zu Fehlzuordnungen und angefochtener Lohnabrechnung.",
      },
      {
        title: "Wetterbedingte Ausfälle sind schwer zu dokumentieren",
        body: "Schlechtwetterstillstand ist im Garten- und Landschaftsbau ein reales Ereignis. Ohne eine digitale Aufzeichnung ist das Dokumentieren von Ausfällen und das Anpassen der Bezahlung langsam und fehleranfällig.",
      },
      {
        title: "Kilometer zwischen Standorten bleiben unerfasst",
        body: "Firmentransporter, Privatfahrzeuge und Fahrgemeinschaften kreuzen den ganzen Tag zwischen den Standorten. Ohne automatische Erfassung schaffen es diese Kilometer nie in das Kostenbild.",
      },
    ],
    features: [
      {
        title: "Teamübersicht über mehrere Standorte",
        body: "Sehen Sie alle Teams, alle Standorte in Echtzeit. Wissen Sie, wer gerade wo arbeitet — nicht erst zum Monatsende.",
      },
      {
        title: "Schichtplanung für Saisonteams",
        body: "Planen Sie, wer wo arbeitet, handhaben Sie kurzfristige Änderungen und übertragen Sie den Plan auf die Telefone des Teams — ohne einen Anruf bei jeder Person.",
        plan: "Time Pro",
      },
      {
        title: "Individuelle Abwesenheitsarten",
        body: "Erstellen Sie individuelle Abwesenheitsarten für Ihre Branche: saisonale Wetterausfälle, Elternzeit, Krankheitstage. Mitarbeitende laden die Dokumentation digital aus der App hoch.",
      },
      {
        title: "Kilometer mit Fahrgemeinschaftserfassung",
        body: "Konfigurieren Sie Transportarten (Firmentransporter, Privatauto, Fahrgemeinschaft Fahrer vs. Beifahrer) mit jeweils eigenen Sätzen. Kilometer werden automatisch pro Fahrt protokolliert.",
        plan: "Add-on",
      },
      {
        title: "Überstundenerfassung mit Umwandlung in Urlaub",
        body: "Überstunden in der Hochsaison summieren sich schnell. Jeder Mitarbeitende kann wählen, sich auszahlen zu lassen oder die Stunden als Freizeit anzusparen — transparent erfasst.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Digitale Zeiterfassung",
      title: "Standortverifizierter Check-in und Abwesenheitserfassung für Saison-Landschaftsbauteams",
      body: "Done-it gibt Garten- und Landschaftsbauteams digitale Zeiterfassung über alle Standorte und Saisonkräfte hinweg — standortverifizierter Check-in, individuelle Abwesenheitsarten für Wetterausfälle und ein sauberer Lohnexport, der jede lokale Anforderung erfüllt.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Team checkt am ersten Standort ein", body: "GPS protokolliert die Ankunft. Stunden werden automatisch auf den richtigen Standort und das richtige Projekt aufgeteilt — keine manuelle Zuordnung am Ende des Tages." },
      { step: "Wetter stoppt die Arbeit", body: "Der Vorarbeiter erfasst den Ausfall aus der App — eine saubere digitale Aufzeichnung, kein zusätzlicher Papierkram." },
      { step: "Monatsende: alle Standorte, alle Teams, ein Export", body: "Stunden, Abwesenheiten und Kilometer über jeden Standort und jedes Teammitglied hinweg werden sauber für die Lohnabrechnung exportiert." },
    ],
    stats: [
      { value: "Automatisch", label: "Kilometererfassung — kein Fahrtenbuch mehr" },
      { value: "Live", label: "Teamübersicht über alle Standorte" },
      { value: "1 Klick", label: "Lohnexport für alle Standorte und alle Teams" },
    ],
    quote: {
      text: "Die Hochsaison bedeutete früher Lohnchaos. Jetzt prüft unser Vorarbeiter die App, jeder ist erfasst, und ich exportiere die Stunden am letzten Tag des Monats.",
      who: "Inhaber, Garten- und Landschaftsbauunternehmen · 12 Saisonkräfte",
      result: "Keine Lohnrekonstruktion zum Monatsende mehr",
    },
    relatedSlugs: ["maintenance", "construction", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },

  {
    slug: "maintenance",
    name: "Wartung",
    headline: "Zeiterfassung für Wartungsteams — wiederkehrende Standortbesuche, Kosten pro Vertrag und Anwesenheitsnachweis in einer App",
    intro: "Wiederkehrende Wartungsverträge stehen und fallen mit der Kostentransparenz. Done-it protokolliert jeden Besuch, erfasst die Stunden gegen das Vertragsbudget und gibt Ihnen — und Ihren Kunden — einen Anwesenheitsnachweis für jeden Einsatz.",
    pains: [
      {
        title: "Keine Kostentransparenz pro Wartungsvertrag",
        body: "Wenn die Stunden für wiederkehrende Besuche nicht gegen den spezifischen Vertrag erfasst werden, wissen Sie erst beim Jahresendreview, ob ein Kunde rentabel ist — und zu diesem Zeitpunkt ist es zu spät für eine Neukalkulation.",
      },
      {
        title: "Kunden erwarten einen Nachweis darüber, was getan wurde",
        body: "Facility-Manager und Immobilieneigentümer wünschen sich zunehmend Anwesenheitsaufzeichnungen: wer kam, wann er ankam, was er tat. Ein Papierprotokoll, das Wochen später vorgelegt wird, erfüllt keine SLA-Anforderungen.",
      },
      {
        title: "Wiederkehrende Besuche schwer zu planen und zu protokollieren",
        body: "Rotierende Schichten über viele wiederkehrende Standorte hinweg — mit Personal, das sich gegenseitig vertritt — schaffen ein Planungs- und Stundenerfassungs-Puzzle, das manuelle Prozesse nicht zuverlässig lösen können.",
      },
      {
        title: "Wetterbedingte Ausfälle betreffen auch die Außenwartung",
        body: "Grünflächenpflege, Fassadenwartung und Außenanlagenarbeiten sind alle von wetterbedingten Ausfällen betroffen. Sie korrekt zu dokumentieren erfordert einen digitalen Nachweis.",
      },
    ],
    features: [
      {
        title: "Vertragsbasierte Kostenerfassung",
        body: "Jeder Wartungsvertrag ist ein Projekt. Die Stunden werden automatisch dagegen protokolliert und geben Ihnen eine Live-Kostenzahl und einen Reststunden-Countdown für jeden Kunden.",
        plan: "Project Basic",
      },
      {
        title: "GPS-bestätigter Anwesenheitsnachweis",
        body: "Mit Zeitstempel versehene, standortverifizierte Check-ins sind eine unanfechtbare Aufzeichnung darüber, wer wann und wie lange vor Ort war — bereit, auf Anfrage mit Kunden geteilt zu werden.",
      },
      {
        title: "Updates mit Fotos vom Standort",
        body: "Mitarbeitende melden Probleme, erledigte Aufgaben oder zusätzliche Arbeiten direkt aus der App — mit Text und Fotos. Kunden erhalten eine Echtzeit-Aufzeichnung; Sie haben die Dokumentation für die Abrechnung.",
        plan: "Project Basic",
      },
      {
        title: "Planung wiederkehrender Schichten",
        body: "Planen Sie wiederkehrende Besuche über alle Verträge hinweg, weisen Sie Mitarbeitende zu und handhaben Sie Vertretungen — ohne einen Anruf bei jeder Person.",
        plan: "Time Pro",
      },
      {
        title: "Abwesenheitsarten für die Außenwartung",
        body: "Konfigurieren Sie Wetterausfälle und andere Abwesenheitsarten. Mitarbeitende erfassen sie aus der App; die Dokumentation wird digital hochgeladen.",
      },
    ],
    compliance: {
      label: "Vertragsbasierte Zeiterfassung",
      title: "Live-Kostentransparenz pro Wartungsvertrag",
      body: "Done-it gibt Wartungsteams GPS-bestätigten Anwesenheitsnachweis pro Vertrag, individuelle Abwesenheitsarten und eine Live-Kostenansicht pro wiederkehrendem Kunden — und erfüllt jede lokale Anforderung an die Zeiterfassung ohne zusätzliche Hardware.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Mitarbeitender kommt am Wartungsstandort an", body: "Der standortverifizierte Check-in protokolliert die Ankunftszeit und verknüpft die Stunden mit dem korrekten Vertrag. Der Anwesenheitsnachweis wird automatisch erstellt." },
      { step: "Aufgaben erledigt, Probleme protokolliert", body: "Erledigte Arbeiten und etwaige Probleme werden aus der App mit Fotos gemeldet. Kunden können genau sehen, was getan wurde." },
      { step: "Kosten pro Vertrag sofort sichtbar", body: "Stunden summieren sich in Echtzeit gegen das Vertragsbudget. Sie kennen die Marge, bevor der Vertrag verlängert wird." },
    ],
    stats: [
      { value: "Live", label: "Kosten pro Wartungsvertrag" },
      { value: "GPS", label: "Anwesenheitsnachweis, auf Anfrage bereit für Kunden" },
      { value: "0", label: "Vertragsverlängerungen, ohne Ihre Marge zu kennen" },
    ],
    quote: {
      text: "Wir können einem Facility-Manager in zwei Minuten genau sagen, wer wann vor Ort war. Dieses Maß an Nachweis hat verändert, wie Kunden uns sehen.",
      who: "Betriebsleiter, Wartungsunternehmen · 8 Mitarbeitende",
      result: "Kundenstreitigkeiten auf null reduziert",
    },
    relatedSlugs: ["cleaning", "landscaping", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },
  {
    slug: "retail",
    name: "Geschäfte & Einzelhandel",
    headline: "Zeiterfassung für Geschäfte – Schichtbeginn, Vertretung und ein lohnfertiger Export in einer App",
    intro: "Frühschichten, Spätschichten und ständig vertritt jemand jemanden. Done-it gibt jedem Geschäft eine saubere digitale Aufzeichnung darüber, wer welche Schicht gearbeitet hat – erfasst über das eigene Handy, zum Monatsende fertig für die Lohnabrechnung.",
    pains: [
      {
        title: "Schichttausch kommt nie in der Lohnabrechnung an",
        body: "Jemand vertritt eine kranke Kollegin, jemand bleibt zum Feierabend eine Stunde länger. Das landet auf einem Zettel oder gar nicht – und die Lohnabrechnung stimmt schon nicht, bevor Sie anfangen.",
      },
      {
        title: "Mehrere Geschäfte, eine Lohnabrechnung",
        body: "Mitarbeitende wechseln zwischen Filialen, Teilzeitkräfte arbeiten an zwei Standorten. Stunden je Geschäft zu sammeln und von Hand zusammenzuführen kostet jeden Monat einen Tag.",
      },
      {
        title: "Pausen und Überstunden bleiben Schätzung",
        body: "Gesetzliche Pausen werden aus dem Gedächtnis abgezogen und Mehrstunden formlos geregelt – genau die zwei Punkte, nach denen eine Prüfung zuerst fragt.",
      },
    ],
    features: [
      {
        title: "Erfassung direkt auf der Fläche",
        body: "Ein Tippen auf dem eigenen Handy zum Schichtbeginn. Kein Terminal an der Kasse, keine gemeinsame Karte, kein Klemmbrett an der Lagertür.",
      },
      {
        title: "Stunden je Geschäft, Personal über Filialen hinweg",
        body: "Jedes Geschäft ist ein Projekt. Wer eine andere Filiale vertritt, erfasst seine Zeit auf diese Filiale – Sie sehen die Stunden dort, wo sie tatsächlich gearbeitet wurden.",
      },
      {
        title: "Pausen automatisch abgezogen",
        body: "Pausenregel einmal festlegen, sie gilt für jede Schicht. Überstunden werden erfasst, sobald sie entstehen, und jede Person wählt Auszahlung oder Freizeitausgleich.",
      },
    ],
    compliance: {
      label: "Schichtbasierte Zeiterfassung",
      title: "Ein belastbarer Nachweis über jede gearbeitete Schicht",
      body: "Done-it führt eine objektive digitale Aufzeichnung jeder Kommen- und Gehen-Buchung, inklusive Pausen und Überstunden – genau den Nachweis, den eine Prüfung oder ein Streit über die Lohnabrechnung verlangt.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Team stempelt zur Schicht ein", body: "Ein Tippen auf dem Handy zum Schichtbeginn. Vertretung in einer anderen Filiale wird auf diese Filiale gebucht." },
      { step: "Sie sehen je Geschäft, wer da ist", body: "Ein Live-Überblick über alle Standorte – eine unbesetzte Frühschicht fällt auf, bevor geöffnet wird." },
      { step: "Lohnabrechnung in einem Export", body: "Stunden, Pausen und Überstunden je Mitarbeitendem und je Geschäft, fertig exportiert für Ihren Lohndienstleister." },
    ],
    stats: [
      { value: "1 Tipp", label: "zum Einstempeln – kein Terminal an der Kasse" },
      { value: "Live", label: "Überblick, wer da ist – in jedem Geschäft" },
      { value: "0", label: "Schichttausche gehen auf dem Weg zur Lohnabrechnung verloren" },
    ],
    quote: {
      text: "Zwei Geschäfte und viele Teilzeitkräfte. Früher wurde die Hälfte der Vertretungsschichten aus dem Gedächtnis abgerechnet. Jetzt liegt jede Stunde auf dem richtigen Geschäft und die Lohnabrechnung dauert Minuten.",
      who: "Inhaber, Einzelhandel · 11 Mitarbeitende",
      result: "Lohnvorbereitung von einem Tag auf unter eine Stunde",
    },
    relatedSlugs: ["hospitality", "beauty", "offices"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "offices",
    name: "Büros & Dienstleistung",
    headline: "Zeiterfassung für Büros – Stunden je Kunde und Projekt, ohne Tabelle",
    intro: "Steuerberatungen, Notariate, Architekturbüros, Agenturen und Beratungen rechnen ihre Zeit ab – oder müssen zumindest wissen, wohin sie geflossen ist. Done-it erfasst Stunden auf dem richtigen Kunden und Projekt, vom Schreibtisch aus und unterwegs.",
    pains: [
      {
        title: "Abrechenbare Stunden, freitags rekonstruiert",
        body: "Zeit, die am Ende der Woche aufgeschrieben wird, ist geschätzte Zeit. Ganze Stunden verschwinden still zwischen der Arbeit und der Rechnung.",
      },
      {
        title: "Niemand weiß, was ein Kunde wirklich kostet",
        body: "Das Honorar wurde vor Monaten vereinbart. Ob die Arbeit noch hineinpasst, zeigt sich erst, wenn jemand nachrechnet – meist zu spät.",
      },
      {
        title: "Hybrides Arbeiten macht den Nachweis lückenhaft",
        body: "Ein Teil des Teams sitzt im Büro, ein Teil zu Hause, ein Teil beim Kunden. Anwesenheit ist dann keine Tatsache mehr, sondern eine Rekonstruktion.",
      },
    ],
    features: [
      {
        title: "Stunden auf Kunde und Projekt",
        body: "Jeder Eintrag landet auf einem Projekt. So sehen Sie die Stunden je Kunde, ohne etwas zu exportieren oder jemanden zu fragen.",
      },
      {
        title: "Am Schreibtisch und außer Haus",
        body: "Dieselbe App auf dem Handy oder im Browser. Ein Kundentermin zählt als Arbeit für diesen Kunden, auf Wunsch inklusive Fahrzeit.",
      },
      {
        title: "Überstunden und Urlaub an einem Ort",
        body: "Mehrstunden werden erfasst, sobald sie entstehen, Urlaub wird in der App beantragt und genehmigt – keine parallele Tabelle darüber, wer wann frei hat.",
      },
    ],
    compliance: {
      label: "Zeiterfassung je Kunde",
      title: "Ein objektiver Nachweis der geleisteten Arbeitszeit",
      body: "Done-it führt eine digitale, manipulationssichere Aufzeichnung der Arbeitszeit je Mitarbeitendem – die Grundlage für die Lohnabrechnung und für eine belastbare Kundenrechnung.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Kundenprojekt anlegen", body: "Einmal einrichten, mit dem Kunden und – wenn Sie nach Budget abrechnen – den geplanten Stunden." },
      { step: "Das Team bucht seine Zeit darauf", body: "Vom Schreibtisch oder vom Handy. Jede Stunde landet auf einem Kunden, nicht im Gedächtnis." },
      { step: "Stunden je Kunde sehen, für die Lohnabrechnung exportieren", body: "Live-Summen je Projekt und zum Monatsende ein sauberer Export für Lohnabrechnung oder Rechnungsstellung." },
    ],
    stats: [
      { value: "Je Kunde", label: "Stunden, sichtbar während die Arbeit noch läuft" },
      { value: "Büro oder unterwegs", label: "derselbe Nachweis, Handy und Browser" },
      { value: "1 Export", label: "für Lohnabrechnung und Rechnungsstellung" },
    ],
    quote: {
      text: "Früher haben wir jeden Freitag die Woche neu zusammengebaut. Jetzt liegen die Stunden schon auf dem richtigen Kunden und ich sehe, welche Akten das Honorar auffressen.",
      who: "Partner, Steuerkanzlei · 8 Mitarbeitende",
      result: "Jeden Monat zurückgewonnene abrechenbare Stunden",
    },
    relatedSlugs: ["retail", "healthcare", "wellness"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "beauty",
    name: "Salons & Körperpflege",
    headline: "Zeiterfassung für Salons – Schichten, Teilzeitstunden und Lohnabrechnung ohne Terminbuch auf Papier",
    intro: "Friseursalons, Barbershops, Nagel- und Kosmetikstudios arbeiten mit Schichten und Teilzeitverträgen. Done-it erfasst, wer wann gearbeitet hat, zieht Pausen automatisch ab und übergibt der Lohnabrechnung eine saubere Datei.",
    pains: [
      {
        title: "Teilzeitverträge, endlose Varianten",
        body: "Diese Woche drei Tage, nächste Woche vier, ein Samstag gegen einen Dienstag getauscht. Vereinbarte und tatsächlich geleistete Stunden laufen schnell auseinander.",
      },
      {
        title: "Das Terminbuch ist keine Zeiterfassung",
        body: "Termine zeigen, was gebucht war, nicht wer gearbeitet hat. Wenn die Lohnabrechnung oder eine Prüfung fragt, ist das Buchungssystem die falsche Antwort.",
      },
      {
        title: "Mehrstunden formlos geregelt",
        body: "Für den letzten Kunden länger zu bleiben wird erinnert, versprochen und manchmal vergessen. Aus solchen Lücken werden Streitfälle.",
      },
    ],
    features: [
      {
        title: "Einstempeln im Salon",
        body: "Ein Tippen auf dem eigenen Handy zum Schichtbeginn – kein gemeinsames Terminal am Empfang, keine Unterschriftenliste am Spiegel.",
      },
      {
        title: "Pausen und Teilzeitregeln einmal festlegen",
        body: "Pausenregel und Sollstunden einmal einstellen; sie gelten für jede Schicht, mit Ausnahmen pro Person, wo ein Vertrag abweicht.",
      },
      {
        title: "Überstunden: auszahlen oder abfeiern",
        body: "Mehrstunden werden erfasst, während sie entstehen, und jede Person wählt Auszahlung oder Freizeitausgleich – geregelt in der App, nicht aus dem Gedächtnis.",
      },
    ],
    compliance: {
      label: "Digitale Zeiterfassung",
      title: "Ein Nachweis, der über das Terminbuch hinausgeht",
      body: "Done-it erfasst die tatsächliche Arbeitszeit je Mitarbeitendem, mit Pausen und Überstunden – getrennt von den Buchungen und belastbar, wenn geprüft wird.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Team stempelt zur Schicht ein", body: "Ein Tippen bei Ankunft. Pausen zieht die Regel ab, die Sie einmal festgelegt haben." },
      { step: "Sie sehen den Tag, während er läuft", body: "Wer da ist, wer Pause macht, wer länger geblieben ist – live, ohne nachzufragen." },
      { step: "Die Lohnabrechnung bekommt eine saubere Datei", body: "Geleistete Stunden, Pausen und Überstunden je Mitarbeitendem, exportiert für Ihren Lohndienstleister." },
    ],
    stats: [
      { value: "1 Tipp", label: "zum Einstempeln – nichts geteilt, nichts auf Papier" },
      { value: "Auto", label: "Pausenabzug bei jeder Schicht" },
      { value: "0", label: "Überstunden, die aus dem Gedächtnis abgerechnet werden" },
    ],
    quote: {
      text: "Alle arbeiten Teilzeit und jede Woche ist anders. Die App erfasst es einfach – ich baue den Monat nicht mehr in einer Tabelle nach.",
      who: "Inhaberin, Friseursalon · 6 Mitarbeitende",
      result: "Keine strittigen Stunden mehr zum Monatsende",
    },
    relatedSlugs: ["wellness", "retail", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "wellness",
    name: "Gesundheit & Wellness",
    headline: "Zeiterfassung für Fitnessstudios, Studios und Spas – Schichten, Kurse und Lohnabrechnung in einer App",
    intro: "Fitnessstudios, Kursstudios, Physiotherapiepraxen und Spas haben lange Öffnungszeiten und wechselndes Personal. Done-it erfasst, wer welche Schicht übernommen hat, zieht Pausen automatisch ab und exportiert die Lohnabrechnung sauber.",
    pains: [
      {
        title: "Lange Öffnungszeiten, wechselnde Vertretung",
        body: "Frühschichten, Abendkurse und Wochenenden, besetzt aus einer Mischung von Vollzeit, Teilzeit und Freiberuflichen. Der Dienstplan ist eine Planung, kein Nachweis.",
      },
      {
        title: "Kursstunden und Stunden auf der Fläche verschwimmen",
        body: "Eine Trainerin gibt zwei Kurse und übernimmt dazwischen den Empfang. Das korrekt zu bezahlen heißt zu wissen, was tatsächlich gearbeitet wurde, nicht was geplant war.",
      },
      {
        title: "Mehrere Standorte, eine Verwaltung",
        body: "Personal wechselt zwischen Studios. Die Stunden jeden Monat von Hand zusammenzuführen ist langsam und fehleranfällig.",
      },
    ],
    features: [
      {
        title: "Einstempeln je Studio",
        body: "Ein Tippen auf dem Handy an dem Standort, an dem gearbeitet wird. Vertretung in einem anderen Studio landet auf dessen Stunden.",
      },
      {
        title: "Live sehen, wer da ist",
        body: "Die Fläche an allen Standorten in Echtzeit – eine unbesetzte Frühschicht sehen Sie, bevor geöffnet wird, nicht danach.",
      },
      {
        title: "Pausen und Überstunden erledigt",
        body: "Pausenregeln werden automatisch angewendet, Mehrstunden erfasst, sobald sie entstehen, mit Auszahlung oder Freizeitausgleich je Mitarbeitendem.",
      },
    ],
    compliance: {
      label: "Schichterfassung für mehrere Standorte",
      title: "Ein objektiver Nachweis für jede Schicht und jeden Standort",
      body: "Done-it führt eine digitale Aufzeichnung der Arbeitszeit je Mitarbeitendem und je Standort – die Grundlage für korrekte Bezahlung und für jede Prüfung der geleisteten Stunden.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Team stempelt im Studio ein", body: "Ein Tippen zum Schichtbeginn, an dem Standort, an dem gearbeitet wird." },
      { step: "Vertretung ist live sichtbar", body: "Wer gerade an welchem Standort ist, damit Lücken geschlossen werden, bevor Mitglieder es merken." },
      { step: "Ein Export für die Lohnabrechnung", body: "Stunden, Pausen und Überstunden je Mitarbeitendem und je Studio, fertig für den Lohndienstleister." },
    ],
    stats: [
      { value: "Je Studio", label: "Stunden, auch wenn anderswo vertreten wird" },
      { value: "Live", label: "Überblick über die Fläche an allen Standorten" },
      { value: "Auto", label: "Pausen und Überstunden, ohne Rechnen von Hand" },
    ],
    quote: {
      text: "Zwei Studios, lange Öffnungszeiten und viele Vertretungsschichten. Jetzt landen die Stunden am richtigen Standort und die Lohnabrechnung ist eine Datei.",
      who: "Leiter, Fitnessstudio · 14 Mitarbeitende",
      result: "Dienstplan und Lohnabrechnung stimmen endlich überein",
    },
    relatedSlugs: ["beauty", "healthcare", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "hospitality",
    name: "Gastronomie & Hotellerie",
    headline: "Zeiterfassung für Cafés, Restaurants und Hotels – Schichten, Pausen und Überstunden, fair abgerechnet",
    intro: "Geteilte Dienste, später Feierabend und ein voller Samstag, der überzieht. Done-it erfasst die Stunden so, wie sie tatsächlich gearbeitet werden, zieht Pausen nach Ihrer Regel ab und gibt der Lohnabrechnung eine saubere Datei.",
    pains: [
      {
        title: "Die Schicht endet nie, wenn der Dienstplan es sagt",
        body: "Der Service zieht sich, das Abschließen dauert noch eine halbe Stunde. Diese Minuten sind echte Arbeit, und sie aus dem Gedächtnis zu regeln ist der Anfang jedes Streits.",
      },
      {
        title: "Geteilte Dienste sind schwer zu erfassen",
        body: "Mittagsservice, Pause, dann Abendservice. Auf Papier wird daraus ein langer Block oder zwei Schätzungen – beides stimmt nicht.",
      },
      {
        title: "Hohe Fluktuation, ständige Einarbeitung",
        body: "Jede Saison neues Personal. Ein System, das Schulung braucht, überlebt die erste volle Woche nicht.",
      },
    ],
    features: [
      {
        title: "Ein- und Ausstempeln je Service",
        body: "Ein Tippen zu Beginn und am Ende jedes Blocks, damit geteilte Dienste so erfasst werden, wie sie stattgefunden haben – nicht zu einem Block zusammengefasst.",
      },
      {
        title: "Pausen nach Ihrer Regel",
        body: "Pflichtpausen werden automatisch abgezogen, mit Ausnahmen pro Person, wo ein Vertrag es verlangt. Kein Rechnen von Hand zum Monatsende.",
      },
      {
        title: "Überstunden in der App geregelt",
        body: "Die halbe Stunde nach dem Abschließen wird erfasst, sobald sie gearbeitet ist, und ausgezahlt oder abgefeiert – die Mitarbeitenden entscheiden.",
      },
    ],
    compliance: {
      label: "Zeiterfassung für geteilte Dienste",
      title: "Ein belastbarer Nachweis in einem Team mit hoher Fluktuation",
      body: "Done-it führt eine objektive digitale Aufzeichnung jeder Schicht, jeder Pause und jeder Mehrstunde – genau das, was eine Lohnprüfung oder eine Arbeitszeitkontrolle sehen will.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Team stempelt zum Service ein", body: "Ein Tippen zu Beginn des Blocks, dasselbe am Ende. Geteilte Dienste bleiben getrennt." },
      { step: "Sie sehen die Fläche live", body: "Wer für den heutigen Abendservice da ist, wer noch Pause macht, wer über den Feierabend hinaus geblieben ist." },
      { step: "Die Lohnabrechnung bekommt die echten Stunden", body: "Arbeitszeit, Pausen und Überstunden je Mitarbeitendem, exportiert in einer Datei." },
    ],
    stats: [
      { value: "Geteilte Dienste", label: "als zwei Blöcke erfasst, nicht als eine Schätzung" },
      { value: "Auto", label: "Pausenabzug nach Ihrer Regel" },
      { value: "1 Min.", label: "zum Einarbeiten neuer Kräfte – es ist ihr eigenes Handy" },
    ],
    quote: {
      text: "Das Abschließen zieht sich immer. Jetzt werden diese Minuten erfasst statt diskutiert, und das Team kann sie abfeiern.",
      who: "Inhaber, Restaurant · 17 Mitarbeitende",
      result: "Weniger Streit, schnellere Lohnabrechnung",
    },
    relatedSlugs: ["retail", "wellness", "beauty"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "healthcare",
    name: "Gesundheit & Pflege",
    headline: "Zeiterfassung für Pflegeteams – Schichten, Hausbesuche und Qualifikationen in einem Nachweis",
    intro: "Praxen, ambulante Pflegeteams und Pflegeheime laufen über Schichten, Besuche und strenge Dokumentation. Done-it erfasst die Arbeitszeit je Schicht und je Besuch und bewahrt Mitarbeiterdokumente dort auf, wo Sie sie finden.",
    pains: [
      {
        title: "Besuche und Schichten in getrennten Systemen",
        body: "Hausbesuche liegen in einem Tool, Dienstplanstunden in einem anderen, und die Lohnabrechnung bekommt eine dritte Version. Das abzugleichen ist ein Monatsjob.",
      },
      {
        title: "Nacht- und Wochenendstunden falsch bewertet",
        body: "Zuschlagszeiten werden anders vergütet. Wenn der Nachweis ungefähr ist, ist es die Bezahlung auch – und die Korrektur kommt später.",
      },
      {
        title: "Zertifikate laufen unbemerkt ab",
        body: "Qualifikationen und Nachweise zählen in der Pflege, und sie liegen in einem Ordner, den niemand öffnet, bis eine Prüfung danach fragt.",
      },
    ],
    features: [
      {
        title: "Einstempeln je Schicht oder je Besuch",
        body: "Ein Tippen zum Schichtbeginn oder an jeder Adresse in der ambulanten Pflege – mit Standortprüfung, wo Sie den Nachweis brauchen.",
      },
      {
        title: "Stunden getrennt, so wie Sie sie vergüten",
        body: "Nacht-, Wochenend- und Regelstunden werden getrennt erfasst, sodass der Lohnexport bereits abbildet, wie die Arbeit tatsächlich bezahlt wird.",
      },
      {
        title: "Mitarbeiterdokumente an einem Ort",
        body: "Verträge, Zertifikate und Ausweise liegen bei der Person, zu der sie gehören – in Sekunden gefunden, wenn jemand danach fragt.",
      },
    ],
    compliance: {
      label: "Schicht- & Besuchserfassung",
      title: "Nachweise, die eine Prüfung überstehen",
      body: "Done-it führt eine digitale, objektive Aufzeichnung der Arbeitszeit je Mitarbeitendem – Stunden, Pausen und die Aufteilung der Zuschlagszeiten – zusammen mit den Dokumenten, die zu jeder Person gehören.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Zur Schicht oder zum Besuch einstempeln", body: "Ein Tippen am Standort oder an der Adresse der betreuten Person, mit Standortprüfung, wo ein Nachweis nötig ist." },
      { step: "Stunden landen in der richtigen Kategorie", body: "Regel-, Nacht- und Wochenendstunden werden getrennt erfasst, während sie entstehen." },
      { step: "Export für die Lohnabrechnung", body: "Eine saubere Datei je Zeitraum, bereits so aufgeteilt, wie Sie vergüten." },
    ],
    stats: [
      { value: "Je Besuch", label: "oder je Schicht – so, wie Ihr Team arbeitet" },
      { value: "Getrennt", label: "Nacht- und Wochenendstunden, erfasst wie gearbeitet" },
      { value: "Abgelegt", label: "Zertifikate je Mitarbeitendem gespeichert" },
    ],
    quote: {
      text: "Hausbesuche und Dienstplanschichten liegen endlich im selben Nachweis. Die Lohnabrechnung ist kein Abgleich mehr.",
      who: "Koordinatorin, ambulantes Pflegeteam · 22 Mitarbeitende",
      result: "Ein Nachweis statt drei",
    },
    relatedSlugs: ["wellness", "offices", "cleaning"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },
];
